import type { Granularity } from "./timeRange";

/**
 * The Title Case rule for chart headings, and the one place it is written down.
 *
 * EVERY WORD OF A CHART TITLE IS CAPITALIZED, prepositions included — `Total In
 * Each Stage`, not `Total in Each Stage`. Jared, 2026-08-25, reading `Highest
 * stage funnel` aloud: *"all chart titles should just be capitalized, first
 * letter"*, and in the same breath *"capital S, capital F"* — the SECOND and
 * THIRD words. Read as "the first letter of the title" it says nothing, since
 * every title already satisfied that. Confirmed with Jose on 2026-09-01 over
 * Chicago/AP, which would lowercase `in`, `by` and `over`. Trello #729 cl.4.
 *
 * Static titles are NOT routed through here — they are literals at their JSX
 * call sites, where a reader sees the words the panel renders. This module is
 * for the one title that interpolates, and for the assertion that enforces the
 * rule on all four surfaces that render these headings.
 */

/**
 * Capitalizes the bucket noun FOR A TITLE ONLY.
 *
 * The same value feeds tooltips, subtitles and the charts' own aria sentences,
 * which are prose and stay lowercase (`…entered each stage that week`). Hence a
 * call at the title rather than a transform where `granularity` is derived.
 *
 * Takes `Granularity`, not `string`, so the closed set is structural: this
 * capitalizes ONE word, and `titleCaseNoun("stage funnel")` would return
 * `"Stage funnel"` — breaking the rule this file states.
 */
export function titleCaseNoun(noun: Granularity): string {
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

/**
 * The words in `text` that break the rule, or `[]` when it holds.
 *
 * Test-only today, but it lives beside the rule it enforces rather than in a
 * test folder: three test files and a Python twin implement this one
 * definition, and splitting the statement from the check is how the rule
 * drifted in the first place.
 *
 * Two things it must get right, both of which shipped wrong during this PR's
 * own review:
 *
 * 1. STRIP LEADING NON-LETTERS FIRST. `word[0] === word[0].toUpperCase()` is
 *    vacuously true for `"("`, and `/^[a-z]/` does not match `"("` either — so
 *    both pass `(furthest`, and the backend already ships `Highest Stage Funnel
 *    (Furthest Stage Reached, Cumulative)`.
 * 2. SPLIT ON ANY WHITESPACE. `.split(" ")` leaves `"Stage\tfunnel"` a single
 *    token starting with `S`, and made this disagree with the Python twin,
 *    which uses `str.split()`.
 *
 * `\p{Ll}` rather than `[a-z]` so `élan` is caught. A word whose first letter
 * is lowercase is flagged even mid-token, so `nuSearch` would be reported — no
 * title needs that spelling today, and the rule is deliberately literal.
 *
 * Only the LEADING run of non-letters is stripped, so lowercase after internal
 * punctuation is unchecked (`Time-to-Fill` passes). That is the rule as given:
 * per space-delimited word. The Python twin behaves the same.
 *
 * The twin agrees on every input a title can plausibly hold — 27 cases checked
 * against each other, which is how the whitespace bug above was found. One
 * constructed class still differs: Python's `str.islower()` is true for
 * `Other_Lowercase` characters like `ª` (U+00AA), which `\p{Ll}` excludes. No
 * chart title begins with a Unicode ordinal indicator; noted so "they agree"
 * is a bounded claim rather than an absolute one.
 *
 * Returns the offending words, not a boolean, so a failure names them.
 */
export function lowercaseWordsIn(text: string): string[] {
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((word) => /^\p{Ll}/u.test(word.replace(/^[^\p{L}]+/u, "")));
}
